import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, Book, BookBody, ApiChild, FamilyMember, MemberRole } from '@/lib/api'
import { EDUCATIONAL_AREAS, AREA_COLORS, areaColor, educationalAreaLabel, EducationalAreaValue } from '@/lib/constants'
import { useAuthStore } from '@/stores/authStore'
import { useFamilyStore } from '@/stores/familyStore'

// ── Image compression ─────────────────────────────────────────────────────────

const MAX_DIMENSION = 1600
const JPEG_QUALITY = 0.85

function compressImage(file: File): Promise<File> {
  return resizeAndReencode(file)
}

function resizeAndReencode(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(objectUrl)
      let { width, height } = img
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        if (width >= height) {
          height = Math.round((height / width) * MAX_DIMENSION)
          width = MAX_DIMENSION
        } else {
          width = Math.round((width / height) * MAX_DIMENSION)
          height = MAX_DIMENSION
        }
      }
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height)
      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('Image compression failed')); return }
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }))
        },
        'image/jpeg',
        JPEG_QUALITY,
      )
    }
    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Failed to load image')) }
    img.src = objectUrl
  })
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string | null {
  if (!iso) return null
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-birch-200 border-t-birch-600" />
    </div>
  )
}

// ── ImageModal ────────────────────────────────────────────────────────────────

function ImageModal({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div className="relative max-h-full max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <img
          src={src}
          alt={alt}
          className="max-h-[80vh] w-full rounded-xl object-contain shadow-2xl"
        />
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 flex h-8 w-8 items-center justify-center rounded-full bg-white text-gray-600 shadow-md hover:bg-gray-100"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
    </div>
  )
}

// ── BookRow ───────────────────────────────────────────────────────────────────

function BookRow({
  book,
  familyId,
  childId,
  canEdit,
  canDelete,
}: {
  book: Book
  familyId: string
  childId: string
  canEdit: boolean
  canDelete: boolean
}) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [title, setTitle] = useState(book.title)
  const [author, setAuthor] = useState(book.author ?? '')
  const [dateRead, setDateRead] = useState(book.dateRead ?? '')
  const [notes, setNotes] = useState(book.notes ?? '')
  const [educationalAreas, setEducationalAreas] = useState<string[]>(book.educationalAreas ?? [])
  const [isFeatured, setIsFeatured] = useState(book.isFeatured ?? false)
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [lightbox, setLightbox] = useState(false)

  const { mutate: save, isPending: saving } = useMutation({
    mutationFn: () => {
      const body: BookBody = {
        title: title.trim(),
        author: author.trim() || null,
        dateRead: dateRead || null,
        notes: notes.trim() || null,
        educationalAreas: educationalAreas.length > 0 ? educationalAreas : null,
        isFeatured,
      }
      return api.books.update(familyId, childId, book.id, body)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['books', familyId, childId] })
      setEditing(false)
      setError(null)
    },
    onError: (err: Error) => setError(err.message),
  })

  const { mutate: remove, isPending: removing } = useMutation({
    mutationFn: () => api.books.delete(familyId, childId, book.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['books', familyId, childId] })
    },
    onError: (err: Error) => setError(err.message),
  })

  function cancelEdit() {
    setEditing(false)
    setTitle(book.title)
    setAuthor(book.author ?? '')
    setDateRead(book.dateRead ?? '')
    setNotes(book.notes ?? '')
    setEducationalAreas(book.educationalAreas ?? [])
    setIsFeatured(book.isFeatured ?? false)
    setError(null)
  }

  if (editing) {
    return (
      <li className="rounded-lg border border-birch-200 bg-birch-50 p-4">
        <form
          onSubmit={(e) => { e.preventDefault(); setError(null); save() }}
          className="space-y-3"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Author</label>
              <input
                type="text"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Date read</label>
              <input
                type="date"
                value={dateRead}
                onChange={(e) => setDateRead(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
              placeholder="Optional observations or reflections…"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Educational Areas</label>
            <div className="flex flex-wrap gap-1.5">
              {EDUCATIONAL_AREAS.map(({ value, label }) => {
                const selected = educationalAreas.includes(value)
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() =>
                      setEducationalAreas((prev) =>
                        selected ? prev.filter((a) => a !== value) : [...prev, value],
                      )
                    }
                    className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                      selected
                        ? 'bg-birch-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={isFeatured}
              onChange={(e) => setIsFeatured(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-birch-600 focus:ring-birch-500"
            />
            Feature in portfolio
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving || !title.trim()}
              className="rounded-lg bg-birch-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-birch-700 disabled:opacity-50"
            >
              {saving ? (book.reviewStatus === 'pending' ? 'Accepting…' : 'Saving…') : (book.reviewStatus === 'pending' ? 'Accept' : 'Save')}
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </form>
      </li>
    )
  }

  return (
    <li className="rounded-lg border border-gray-100 bg-white px-4 py-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 gap-3">
          {book.mediaAttachments?.length > 0 ? (
            <>
              <div className="relative h-20 w-14 shrink-0">
                <img
                  src={book.mediaAttachments[0]}
                  alt={book.title}
                  className="h-full w-full cursor-zoom-in rounded object-cover"
                  onClick={() => setLightbox(true)}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                />
                {book.reviewStatus === 'pending' && (
                  <div className="absolute bottom-0 left-0 right-0 rounded-b bg-amber-400/90 py-0.5 text-center text-[8px] font-bold uppercase tracking-wide text-amber-900">
                    Review
                  </div>
                )}
              </div>
              {lightbox && (
                <ImageModal
                  src={book.mediaAttachments[0]}
                  alt={book.title}
                  onClose={() => setLightbox(false)}
                />
              )}
            </>
          ) : (
            <div className="relative h-20 w-14 shrink-0 rounded bg-birch-100 flex items-center justify-center">
              <svg className="h-6 w-6 text-birch-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
              </svg>
              {book.reviewStatus === 'pending' && (
                <div className="absolute bottom-0 left-0 right-0 rounded-b bg-amber-400/90 py-0.5 text-center text-[8px] font-bold uppercase tracking-wide text-amber-900">
                  Review
                </div>
              )}
            </div>
          )}
          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline gap-2">
              <p className="font-medium text-gray-900">{book.title}</p>
              {book.reviewStatus === 'pending' && (
                <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                  Needs review
                </span>
              )}
              {book.isFeatured && (
                <span className="inline-flex items-center rounded-full bg-birch-100 px-2 py-0.5 text-[10px] font-semibold text-birch-700">
                  Featured
                </span>
              )}
            </div>
            {book.educationalAreas && book.educationalAreas.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {book.educationalAreas.map((area) => (
                  <span
                    key={area}
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${areaColor(area)}`}
                  >
                    {educationalAreaLabel(area)}
                  </span>
                ))}
              </div>
            )}
            {book.author && <p className="text-sm text-gray-500">{book.author}</p>}
            {formatDate(book.dateRead) && (
              <p className="mt-1 text-xs text-gray-400">{formatDate(book.dateRead)}</p>
            )}
            {book.notes && (
              <p className="mt-2 text-sm italic text-gray-600">"{book.notes}"</p>
            )}
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {confirmDelete ? (
            <>
              <span className="text-xs text-gray-500">Remove?</span>
              <button
                onClick={() => remove()}
                disabled={removing}
                className="rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                {removing ? 'Removing…' : 'Yes'}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              {canEdit && (
                <button
                  onClick={() => setEditing(true)}
                  className={`rounded px-2 py-1 text-xs hover:bg-gray-100 ${book.reviewStatus === 'pending' ? 'font-semibold text-amber-700' : 'text-gray-600'}`}
                >
                  {book.reviewStatus === 'pending' ? 'Review' : 'Edit'}
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="rounded px-2 py-1 text-xs text-red-500 hover:bg-red-50"
                >
                  Remove
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </li>
  )
}

// ── AddBookForm ───────────────────────────────────────────────────────────────

function AddBookForm({ familyId, childId }: { familyId: string; childId: string }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<'manual' | 'photo'>('photo')

  // Manual mode state
  const [title, setTitle] = useState('')
  const [author, setAuthor] = useState('')
  const [dateRead, setDateRead] = useState('')
  const [notes, setNotes] = useState('')
  const [educationalAreas, setEducationalAreas] = useState<string[]>([])
  const [isFeatured, setIsFeatured] = useState(false)

  // Photo mode state
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [photoDate, setPhotoDate] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [compressing, setCompressing] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [error, setError] = useState<string | null>(null)

  function handleClose() {
    setOpen(false)
    setMode('photo')
    setTitle('')
    setAuthor('')
    setDateRead('')
    setNotes('')
    setEducationalAreas([])
    setIsFeatured(false)
    setImageFile(null)
    setImagePreview(null)
    setPhotoDate('')
    setDragOver(false)
    setCompressing(false)
    setError(null)
  }

  async function handleFile(raw: File) {
    setCompressing(true)
    setError(null)
    try {
      const compressed = await compressImage(raw)
      setImageFile(compressed)
      setImagePreview(URL.createObjectURL(compressed))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not process image. Please use a JPEG or PNG.')
    } finally {
      setCompressing(false)
    }
  }

  function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
  }

  const { mutate: create, isPending: creatingManual } = useMutation({
    mutationFn: () =>
      api.books.create(familyId, childId, {
        title: title.trim(),
        author: author.trim() || null,
        dateRead: dateRead || null,
        notes: notes.trim() || null,
        educationalAreas: educationalAreas.length > 0 ? educationalAreas : null,
        isFeatured: isFeatured || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['books', familyId, childId] })
      handleClose()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    onError: (err: Error) => setError(err.message),
  })

  const { mutate: createFromImage, isPending: creatingFromImage } = useMutation({
    mutationFn: () => api.books.createFromImage(familyId, childId, imageFile!, photoDate || null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['books', familyId, childId] })
      handleClose()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    onError: (err: Error) => setError(err.message),
  })

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-birch-600 text-white shadow-lg hover:bg-birch-700 active:scale-95 transition-transform"
        aria-label="Add book"
      >
        <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
      </button>
    )
  }

  const isPending = creatingManual || creatingFromImage || compressing

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4" onClick={handleClose}>
    <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
    <div className="space-y-3">
      <div className="space-y-2">
        <p className="text-center text-sm font-medium text-birch-800">Add a book</p>
        <div className="flex rounded-lg border border-gray-200 bg-white p-0.5 text-sm">
          <button
            type="button"
            onClick={() => { setMode('photo'); setError(null) }}
            className={`flex-1 rounded-md px-4 py-2 font-medium transition-colors ${mode === 'photo' ? 'bg-birch-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Upload photo
          </button>
          <button
            type="button"
            onClick={() => { setMode('manual'); setError(null) }}
            className={`flex-1 rounded-md px-4 py-2 font-medium transition-colors ${mode === 'manual' ? 'bg-birch-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Enter details
          </button>
        </div>
      </div>

      {mode === 'manual' ? (
        <form
          onSubmit={(e) => { e.preventDefault(); setError(null); create() }}
          className="space-y-3"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Charlotte's Web"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Author</label>
              <input
                type="text"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder="E.B. White"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Date read</label>
              <input
                type="date"
                value={dateRead}
                onChange={(e) => setDateRead(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional observations or reflections…"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Educational Areas</label>
            <div className="flex flex-wrap gap-1.5">
              {EDUCATIONAL_AREAS.map(({ value, label }) => {
                const selected = educationalAreas.includes(value)
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() =>
                      setEducationalAreas((prev) =>
                        selected ? prev.filter((a) => a !== value) : [...prev, value],
                      )
                    }
                    className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                      selected
                        ? 'bg-birch-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={isFeatured}
              onChange={(e) => setIsFeatured(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-birch-600 focus:ring-birch-500"
            />
            Feature in portfolio
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={isPending || !title.trim()}
              className="rounded-lg bg-birch-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-birch-700 disabled:opacity-50"
            >
              {creatingManual ? 'Adding…' : 'Add book'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            onChange={handleImageChange}
            className="hidden"
          />
          {compressing ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-birch-600" />
              Processing…
            </div>
          ) : imagePreview ? (
            <div className="flex items-start gap-3">
              <img
                src={imagePreview}
                alt="Book preview"
                className="h-32 w-24 rounded-lg object-cover shadow-sm"
              />
              <button
                type="button"
                onClick={() => { setImageFile(null); setImagePreview(null); if (fileInputRef.current) fileInputRef.current.value = '' }}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Change photo
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault()
                setDragOver(false)
                const file = e.dataTransfer.files?.[0]
                if (file) handleFile(file)
              }}
              className={`flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-sm transition-colors ${dragOver ? 'border-birch-500 bg-birch-50 text-birch-600' : 'border-gray-300 text-gray-500 hover:border-birch-400 hover:text-birch-600'}`}
            >
              <span className="text-2xl">📷</span>
              <span className="sm:hidden">Tap to choose a photo</span>
              <span className="hidden sm:inline">Drag & drop or click to choose a photo</span>
            </button>
          )}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Date read</label>
            <input
              type="date"
              value={photoDate}
              onChange={(e) => setPhotoDate(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={isPending || !imageFile}
              onClick={() => { setError(null); createFromImage() }}
              className="rounded-lg bg-birch-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-birch-700 disabled:opacity-50"
            >
              {creatingFromImage ? 'Uploading…' : 'Upload & add'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
    </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function ReadingListPage() {
  const { user } = useAuthStore()
  const { activeChildId } = useFamilyStore()

  // 1. Profile → familyId
  const { data: profile, isLoading: loadingProfile } = useQuery({
    queryKey: ['me'],
    queryFn: api.me.get,
  })

  const familyId = profile?.familyId ?? null

  // 2. Children (so we can resolve the active child without relying on store)
  const { data: children = [], isLoading: loadingChildren } = useQuery<ApiChild[]>({
    queryKey: ['children', familyId],
    queryFn: () => api.children.list(familyId!),
    enabled: !!familyId,
  })

  // 3. Members → role
  const { data: members = [] } = useQuery<FamilyMember[]>({
    queryKey: ['members', familyId],
    queryFn: () => api.members.list(familyId!),
    enabled: !!familyId,
  })

  // Resolve active child (prefer persisted selection, fall back to first)
  const activeChild: ApiChild | null =
    children.find((c) => c.id === activeChildId) ?? children[0] ?? null
  const childId = activeChild?.id ?? null

  const [areaFilter, setAreaFilter] = useState<EducationalAreaValue | null>(null)
  useEffect(() => { setAreaFilter(null) }, [childId])

  // 4. Books
  const { data: books, isLoading: loadingBooks } = useQuery<Book[]>({
    queryKey: ['books', familyId, childId, areaFilter],
    queryFn: () => api.books.list(familyId!, childId!, areaFilter),
    enabled: !!familyId && !!childId,
  })

  const myRole: MemberRole | undefined = members.find(
    (m) => m.userId === user?.userId,
  )?.role
  const canEdit = myRole === 'owner' || myRole === 'contributor'
  const canDelete = myRole === 'owner'

  const sortedBooks = books
    ? [...books].sort((a, b) => (b.dateRead ?? '').localeCompare(a.dateRead ?? ''))
    : []

  const isLoading = loadingProfile || (!!familyId && loadingChildren)

  // ── No family ──────────────────────────────────────────────────────────────
  if (!isLoading && !familyId) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Reading List</h1>
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="text-gray-500">Set up your family first.</p>
          <Link
            to="/family"
            className="mt-3 inline-block rounded-lg bg-birch-600 px-4 py-2 text-sm font-medium text-white hover:bg-birch-700"
          >
            Go to Family
          </Link>
        </div>
      </div>
    )
  }

  // ── No children ────────────────────────────────────────────────────────────
  if (!isLoading && familyId && !activeChild) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Reading List</h1>
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="text-gray-500">Add a student in Family to start tracking books.</p>
          <Link
            to="/family"
            className="mt-3 inline-block rounded-lg bg-birch-600 px-4 py-2 text-sm font-medium text-white hover:bg-birch-700"
          >
            Go to Family
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reading List</h1>
          {activeChild && (
            <p className="mt-0.5 text-sm text-gray-500">{activeChild.firstName}</p>
          )}
        </div>
        {!isLoading && !loadingBooks && activeChild && (
          <span className="text-sm text-gray-400">
            {sortedBooks.length} book{sortedBooks.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="p-6">
          {!isLoading && !loadingBooks && activeChild && (
            <div className="mb-4 -mx-1 flex gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
              <button
                onClick={() => setAreaFilter(null)}
                className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  areaFilter === null
                    ? 'bg-birch-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {EDUCATIONAL_AREAS.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setAreaFilter(areaFilter === value ? null : value)}
                  className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    areaFilter === value
                      ? AREA_COLORS[value]
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {(isLoading || loadingBooks) && <Spinner />}

          {!isLoading && !loadingBooks && sortedBooks.length === 0 && activeChild && (
            <p className="mb-4 text-sm text-gray-500">No books logged yet.</p>
          )}

          {!isLoading && !loadingBooks && sortedBooks.length > 0 && (
            <ul className="mb-4 space-y-2">
              {sortedBooks.map((book) => (
                <BookRow
                  key={book.id}
                  book={book}
                  familyId={familyId!}
                  childId={childId!}
                  canEdit={canEdit}
                  canDelete={canDelete}
                />
              ))}
            </ul>
          )}

          {!isLoading && !loadingBooks && canEdit && activeChild && (
            <AddBookForm familyId={familyId!} childId={childId!} />
          )}
        </div>
      </div>
    </div>
  )
}
